/**
 * Custom Profile Service
 * Handles extended profile data (photos, bio, skills, certifications, interests)
 *
 * Data from Azure AD (read-only): role, reporting structure, department, office, email, phone
 * User-managed data (editable): photos, bios, skills, interests, licenses/certifications
 *
 * Storage: server-backed via the profiles API when VITE_API_BASE_URL is set
 * (synced into localStorage as a read cache so all reads stay synchronous);
 * pure localStorage in dev/mock mode.
 */

const STORAGE_KEY = 'peoplevue_profiles';
const EXPERTISE_TAGS_KEY = 'peoplevue_expertise_tags';
const API_BASE = import.meta.env.VITE_API_BASE_URL;

// Stashed by syncProfiles so saveProfile can write through to the server
// without every call site having to thread a token down.
let apiToken = null;

/**
 * Hydrate the local profile cache from the server. Call once after sign-in
 * (and after any token refresh). Non-fatal on failure — the app falls back
 * to whatever is cached locally.
 */
export async function syncProfiles(token) {
  if (!API_BASE || !token) return false;
  apiToken = token;
  try {
    const res = await fetch(`${API_BASE}/profiles`, {
      headers: { Authorization: `Bearer ${token}` },
      cache: 'no-store',
    });
    if (!res.ok) throw new Error(`Profiles API ${res.status}`);
    const serverProfiles = await res.json();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(serverProfiles || {}));
    return true;
  } catch (err) {
    console.warn('Failed to sync profiles (non-fatal):', err.message);
    return false;
  }
}

function pushProfile(userId, profile) {
  if (!API_BASE || !apiToken) return;
  fetch(`${API_BASE}/profiles/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiToken}` },
    body: JSON.stringify(profile),
  })
    .then((res) => { if (!res.ok) console.warn(`Profile push failed: ${res.status}`); })
    .catch((err) => console.warn('Profile push failed:', err.message));
}

export const DEFAULT_EXPERTISE_CATEGORIES = [
  {
    id: 'architecture-design',
    name: 'Architecture & Design',
    tags: [
      'Adaptive Reuse', 'Civic / Institutional', 'Commercial Design', 'Conceptual Design',
      'Construction Documents', 'Design Development', 'Design Management', 'Education',
      'Healthcare Design', 'Historic Preservation', 'Hospitality', 'Mixed-Use',
      'Residential', 'Schematic Design', 'Sports & Recreation',
    ],
  },
  {
    id: 'planning-urban',
    name: 'Planning & Urban Design',
    tags: [
      'Climate Resilience', 'Code Compliance', 'Community Engagement', 'Land Use Planning',
      'Landscape Architecture', 'Master Planning', 'Site Analysis',
      'Transit-Oriented Development', 'Urban Design', 'Urban Planning', 'Zoning & Entitlements',
    ],
  },
  {
    id: 'engineering',
    name: 'Engineering',
    tags: [
      'Building Envelope', 'Civil Engineering', 'Concrete Design', 'Electrical Engineering',
      'Energy Systems', 'Fire Protection Engineering', 'Forensic Assessment', 'Foundation Design',
      'Geotechnical Coordination', 'HVAC Design', 'Lighting & Power Distribution',
      'Load Calculations', 'Low Voltage / Technology Design', 'Masonry Design',
      'MEP Engineering', 'Mechanical Engineering', 'Plumbing Engineering', 'Seismic Design',
      'Site / Civil Design', 'Steel Design', 'Structural Engineering', 'Utility Coordination',
    ],
  },
  {
    id: 'construction',
    name: 'Construction Administration',
    tags: [
      'Bidding & Procurement', 'Building Systems', 'Change Order Management',
      'Closeout & Warranty', 'Commissioning', 'Constructability Review',
      'Construction Administration', 'Construction Management', 'Contract Administration',
      'Cost Estimating', 'Field Reports', 'Jobsite Safety / OSHA', 'Owner Representation',
      'Pay Application Review', 'Preconstruction Services', 'Punch List',
      'Quality Control Inspections', 'RFI Management', 'Scheduling / CPM',
      'Site Observation', 'Specifications (CSI)', 'Submittal Review', 'Value Engineering',
    ],
  },
  {
    id: 'interior-design',
    name: 'Interior Design',
    tags: [
      'Corporate Interiors', 'FF&E Specifications', 'Finish Selections', 'Healthcare Interiors',
      'Hospitality Interiors', 'Interior Design', 'Lighting Design', 'Space Planning',
      'Wayfinding', 'Workplace Strategy',
    ],
  },
  {
    id: 'project-management',
    name: 'Project Management',
    tags: [
      'Agile / Scrum', 'Budget Management', 'Client Management',
      'Construction Management at Risk (CMAR)', 'Consultant Coordination', 'Design-Build',
      'Earned Value Management', 'Integrated Project Delivery (IPD)', 'Meeting Facilitation',
      'Multi-Project Portfolio', 'Owner & Stakeholder Coordination', 'Project Controls',
      'Project Delivery', 'Project Management', 'Project Setup & Work Plans', 'QA/QC',
      'Resource Planning', 'Risk Management', 'Schedule Management', 'Staffing & Workload Planning',
    ],
  },
  {
    id: 'sustainability',
    name: 'Sustainability & Resilience',
    tags: [
      'Carbon Neutral Design', 'Daylighting Analysis', 'Energy Modeling', 'LEED',
      'Living Building Challenge', 'Net Zero Energy', 'Passive House',
      'Stormwater Management', 'Sustainability', 'WELL Building Standard',
    ],
  },
  {
    id: 'technology',
    name: 'Technology & Innovation',
    tags: [
      'BIM / Revit', 'Computational Design', 'Data Analytics', 'Digital Fabrication',
      'Drone / Aerial Survey', 'GIS', 'Grasshopper / Rhino', 'Laser Scanning / Point Cloud',
      'Parametric Design', 'VR/AR Visualization',
    ],
  },
  {
    id: 'finance',
    name: 'Finance',
    tags: [
      'Accounts Payable / Receivable', 'Audit & Reporting', 'Budgeting & Forecasting',
      'ERP Systems', 'Fee Proposals & Contracts', 'Financial Analysis',
      'Payroll', 'Project Cost Control', 'Tax Compliance',
    ],
  },
  {
    id: 'hr',
    name: 'Human Resources',
    tags: [
      'Benefits Administration', 'Compensation & Classification', 'Employee Relations',
      'HRIS Systems', 'HR Policy & Compliance', 'Onboarding & Offboarding',
      'Performance Management', 'Recruiting & Talent Acquisition', 'Training & Development',
    ],
  },
  {
    id: 'it',
    name: 'Information Technology',
    tags: [
      'Cloud Services (Azure / AWS)', 'Cybersecurity', 'Data Backup & Recovery',
      'Help Desk & User Support', 'IT Infrastructure', 'Microsoft 365 Administration',
      'Network & Systems Administration', 'Power Platform Administration',
      'SharePoint Administration', 'Software Procurement',
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    tags: [
      'Brand Strategy', 'Content Marketing', 'Copywriting', 'Email Marketing',
      'Event Planning & Tradeshow', 'Market Research', 'SEO / SEM',
      'Social Media Management', 'Video & Multimedia Production',
    ],
  },
  {
    id: 'bd',
    name: 'Business Development',
    tags: [
      'Award Submissions', 'Capture Planning', 'Client Presentations',
      'Client Relationship Management', 'Conference & Networking', 'CRM Systems',
      'Go / No-Go Evaluation', 'Interview Coaching', 'K-12 / Education Pursuits',
      'Lead Generation', 'Market Sector Strategy', 'Municipal / Public Pursuits',
      'Proposal Writing', 'Pursuit Strategy', 'RFP / RFQ Response',
      'Shortlist Interviews', 'Teaming & Partnerships', 'Win Strategy',
    ],
  },
  {
    id: 'sales',
    name: 'Sales',
    tags: [
      'Account Management', 'Client Retention', 'Contract Negotiation',
      'Cross-Selling Services', 'Pipeline Management', 'Pricing Strategy',
      'Prospecting & Outreach', 'Sales Forecasting', 'Sales Presentations',
      'Salesforce / CRM',
    ],
  },
  {
    id: 'development',
    name: 'Real Estate & Development',
    tags: [
      'Bond Planning Support', 'Capital Planning', 'Developer Coordination',
      'Due Diligence', 'Feasibility Studies', 'Land Acquisition Support',
      'Pro Forma / Financial Modeling', 'Public-Private Partnerships (P3)',
      'Site Selection', 'Test Fits',
    ],
  },
  {
    id: 'software-development',
    name: 'Software Development',
    tags: [
      'API Development', 'Azure Functions', 'C# / .NET', 'Database Design / SQL',
      'DevOps / CI-CD', 'Git / Version Control', 'JavaScript / TypeScript',
      'Power Apps / Power Automate', 'Python', 'React', 'SharePoint Development',
      'UI/UX Design', 'Web Development',
    ],
  },
  {
    id: 'creative',
    name: 'Creative Services',
    tags: [
      'Adobe Creative Suite', 'Brand Identity', 'Data Visualization',
      'Graphic Design', 'Illustration & Rendering', 'InDesign',
      'Motion Graphics', 'Photography', 'Presentation Design', 'Wayfinding & Signage',
    ],
  },
];

/**
 * Get all custom profile data from storage
 */
export function getAllProfiles() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (err) {
    console.error('Failed to load profiles:', err);
    return {};
  }
}

/**
 * Get custom profile data for a specific user
 */
export function getProfile(userId) {
  const profiles = getAllProfiles();
  return profiles[userId] || {
    photo: null,
    banner: null,
    bio: '',
    skills: [],
    certifications: [],
    interests: [],
    practiceArea: '',
    statesLicensed: [],
    highSchoolAlmaMater: '',
    collegeAlmaMater: '',
    volunteerAt: '',
    coreExpertise: '',
    areasOfExpertise: [],
    askMeAbout: '',
    moregroupProjects: [],
    priorProjects: '',
    programsExpertise: [],
    openToSpeaking: false,
    openToWhitePaper: false,
    openToMentoring: false,
    mentorTopics: '',
    innovationTopics: '',
  };
}

export function getExpertiseTags() {
  try {
    const data = localStorage.getItem(EXPERTISE_TAGS_KEY);
    if (!data) return DEFAULT_EXPERTISE_CATEGORIES;
    const parsed = JSON.parse(data);
    // Migration: old flat array → wrap as single "General" category, preserving custom tags
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
      const knownTags = new Set(DEFAULT_EXPERTISE_CATEGORIES.flatMap((c) => c.tags));
      const customTags = parsed.filter((t) => !knownTags.has(t));
      const categories = [...DEFAULT_EXPERTISE_CATEGORIES];
      if (customTags.length > 0) {
        categories.push({ id: 'other', name: 'Other', tags: customTags });
      }
      saveExpertiseTags(categories);
      return categories;
    }
    return mergeWithDefaults(parsed);
  } catch { return DEFAULT_EXPERTISE_CATEGORIES; }
}

// A saved taxonomy (admin-edited) would otherwise pin this device to the tag
// set that existed when it was saved. Merge: keep every saved category and
// custom tag, but union in any default categories/tags added since — so
// taxonomy upgrades ship to existing devices without clobbering admin edits.
function mergeWithDefaults(saved) {
  if (!Array.isArray(saved)) return DEFAULT_EXPERTISE_CATEGORIES;
  const byId = new Map(saved.map((category) => [category.id, category]));
  const merged = DEFAULT_EXPERTISE_CATEGORIES.map((def) => {
    const existing = byId.get(def.id);
    if (!existing) return def;
    byId.delete(def.id);
    return { ...existing, tags: [...new Set([...existing.tags, ...def.tags])].sort() };
  });
  // Categories the admin created (or "Other") ride along untouched.
  return [...merged, ...byId.values()];
}

export function saveExpertiseTags(categories) {
  try {
    localStorage.setItem(EXPERTISE_TAGS_KEY, JSON.stringify(categories));
  } catch (err) {
    console.error('Failed to save expertise tags:', err);
  }
}

/**
 * Save user's custom photo (base64 encoded)
 */
export function savePhoto(userId, photoDataUrl) {
  const profile = getProfile(userId);
  profile.photo = photoDataUrl;
  saveProfile(userId, profile);
  return photoDataUrl;
}

/**
 * Get user's custom photo
 */
export function getPhoto(userId) {
  const profile = getProfile(userId);
  return profile.photo || null;
}

/**
 * Save custom profile data for a user
 */
export function saveProfile(userId, profileData) {
  try {
    const profiles = getAllProfiles();
    profiles[userId] = {
      ...profiles[userId],
      ...profileData,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profiles));
    pushProfile(userId, profiles[userId]);
    return true;
  } catch (err) {
    console.error('Failed to save profile:', err);
    return false;
  }
}

/**
 * Add a skill to user's profile
 */
export function addSkill(userId, skill) {
  const profile = getProfile(userId);
  if (!profile.skills.includes(skill)) {
    profile.skills.push(skill);
    saveProfile(userId, profile);
  }
  return profile.skills;
}

/**
 * Remove a skill from user's profile
 */
export function removeSkill(userId, skill) {
  const profile = getProfile(userId);
  profile.skills = profile.skills.filter((s) => s !== skill);
  saveProfile(userId, profile);
  return profile.skills;
}

/**
 * Add a certification to user's profile
 */
export function addCertification(userId, certification) {
  const profile = getProfile(userId);
  profile.certifications.push({
    id: Date.now().toString(),
    ...certification,
  });
  saveProfile(userId, profile);
  return profile.certifications;
}

/**
 * Remove a certification from user's profile
 */
export function removeCertification(userId, certId) {
  const profile = getProfile(userId);
  profile.certifications = profile.certifications.filter((c) => c.id !== certId);
  saveProfile(userId, profile);
  return profile.certifications;
}

/**
 * Update user's bio
 */
export function updateBio(userId, bio) {
  const profile = getProfile(userId);
  profile.bio = bio;
  saveProfile(userId, profile);
  return bio;
}

/**
 * Update user's interests
 */
export function updateInterests(userId, interests) {
  const profile = getProfile(userId);
  profile.interests = interests;
  saveProfile(userId, profile);
  return interests;
}

/**
 * Merge custom profile data with employee data from Graph API
 */
export function mergeProfileData(employees) {
  const profiles = getAllProfiles();
  return employees.map((employee) => ({
    ...employee,
    customProfile: profiles[employee.id] || {
      bio: '',
      skills: [],
      certifications: [],
      interests: [],
    },
  }));
}
